import { FunctionComponent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import styles from './newOpeningHoursDialog.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useMutation } from '@apollo/client'
import { UPDATE_DAYCARE_OPENING } from '@/modules/API/daycare/daycareHandler'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import TimePickerComponent from '@/components/_custom/Picker/timePicker/timePicker'
import moment from 'moment/moment'
import { useAuth } from '@/modules/context/AuthContext'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface newOpeningHoursDialogProps {
    open: boolean
    handleClose: () => void
    daycareId: number
}

const NewOpeningHours: FunctionComponent<newOpeningHoursDialogProps> = ({
    open,
    handleClose,
    daycareId
}: newOpeningHoursDialogProps) => {
    const { t } = useTranslation()

    const { fetchCurrentDaycare } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [checkBreak, setCheckBreak] = useState<boolean>(false)

    const [checkMonOpen, setCheckMonOpen] = useState<boolean>(false)
    const [checkTueOpen, setCheckTueOpen] = useState<boolean>(false)
    const [checkWedOpen, setCheckWedOpen] = useState<boolean>(false)
    const [checkThuOpen, setCheckThuOpen] = useState<boolean>(false)
    const [checkFriOpen, setCheckFriOpen] = useState<boolean>(false)
    const [checkSatOpen, setCheckSatOpen] = useState<boolean>(false)
    const [checkSunOpen, setCheckSunOpen] = useState<boolean>(false)

    const [openHours, setOpenHours] = useState<string | null>('08:00')
    const [closeHours, setCloseHours] = useState<string | null>('17:30')
    const [breakStart, setBreakStart] = useState<string | null>(null)
    const [breakEnd, setBreakEnd] = useState<string | null>(null)

    const [updateDaycare, dataUpdateDaycare] = useMutation(UPDATE_DAYCARE_OPENING, {
        variables: {
            daycare_id: daycareId,
            monOpen: checkMonOpen ? openHours : null,
            monClose: checkMonOpen ? closeHours : null,
            monBreakStart: checkBreak ? breakStart : null,
            monBreakEnd: checkBreak ? breakEnd : null,
            tueOpen: checkTueOpen ? openHours : null,
            tueClose: checkTueOpen ? closeHours : null,
            tueBreakStart: checkBreak ? breakStart : null,
            tueBreakEnd: checkBreak ? breakEnd : null,
            wedOpen: checkWedOpen ? openHours : null,
            wedClose: checkWedOpen ? closeHours : null,
            wedBreakStart: checkBreak ? breakStart : null,
            wedBreakEnd: checkBreak ? breakEnd : null,
            thuOpen: checkThuOpen ? openHours : null,
            thuClose: checkThuOpen ? closeHours : null,
            thuBreakStart: checkBreak ? breakStart : null,
            thuBreakEnd: checkBreak ? breakEnd : null,
            friOpen: checkFriOpen ? openHours : null,
            friClose: checkFriOpen ? closeHours : null,
            friBreakStart: checkBreak ? breakStart : null,
            friBreakEnd: checkBreak ? breakEnd : null,
            satOpen: checkSatOpen ? openHours : null,
            satClose: checkSatOpen ? closeHours : null,
            satBreakStart: checkBreak ? breakStart : null,
            satBreakEnd: checkBreak ? breakEnd : null,
            sunOpen: checkSunOpen ? openHours : null,
            sunClose: checkSunOpen ? closeHours : null,
            sunBreakStart: checkBreak ? breakStart : null,
            sunBreakEnd: checkBreak ? breakEnd : null
        },
        onCompleted: () => {
            fetchCurrentDaycare()

            handleClose()

            showNotificationSuccess(t('daycareOpeningHoursUpdated'))
        },
        onError: () => {
            showNotificationError(t('errorUpdateDaycareOpeningHours'))
        }
    })

    const disabled: boolean =
        dataUpdateDaycare.loading ||
        (!checkMonOpen &&
            !checkTueOpen &&
            !checkWedOpen &&
            !checkThuOpen &&
            !checkFriOpen &&
            !checkSatOpen &&
            !checkSunOpen)

    function setBreakCheck(): void {
        if (!checkBreak) {
            setBreakStart('12:00')
            setBreakEnd('13:00')
        } else {
            setBreakStart(null)
            setBreakEnd(null)
        }
        setCheckBreak(!checkBreak)
    }

    return (
        <Dialog open={open} handleClose={handleClose} className={styles.Dialog}>
            <DialogHeader title={t('addNewOpeningHours')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <div className={styles.CheckboxContainer}>
                        <Checkbox
                            checked={checkMonOpen}
                            onChange={() => setCheckMonOpen(!checkMonOpen)}
                            text={t('monday')}
                            className={styles.Checkbox}
                        />
                        <Checkbox
                            checked={checkTueOpen}
                            onChange={() => setCheckTueOpen(!checkTueOpen)}
                            text={t('tuesday')}
                            className={styles.Checkbox}
                        />
                        <Checkbox
                            checked={checkWedOpen}
                            onChange={() => setCheckWedOpen(!checkWedOpen)}
                            text={t('wednesday')}
                            className={styles.Checkbox}
                        />
                        <Checkbox
                            checked={checkThuOpen}
                            onChange={() => setCheckThuOpen(!checkThuOpen)}
                            text={t('thursday')}
                            className={styles.Checkbox}
                        />
                        <Checkbox
                            checked={checkFriOpen}
                            onChange={() => setCheckFriOpen(!checkFriOpen)}
                            text={t('friday')}
                            className={styles.Checkbox}
                        />
                    </div>
                    <div className={styles.CheckboxContainer}>
                        <Checkbox
                            checked={checkSatOpen}
                            onChange={() => setCheckSatOpen(!checkSatOpen)}
                            text={t('saturday')}
                            className={styles.Checkbox}
                        />
                        <Checkbox
                            checked={checkSunOpen}
                            onChange={() => setCheckSunOpen(!checkSunOpen)}
                            text={t('sunday')}
                            className={styles.Checkbox}
                        />
                    </div>
                    <div className={styles.Input}>
                        <TimePickerComponent
                            value={openHours || '08:00'}
                            setValue={setOpenHours}
                            label={t('from')}
                            minuteToDisable={15}
                            maxHour={Number(
                                moment(checkBreak ? breakStart : closeHours, 'HH:mm')
                                    .subtract('15'.valueOf(), 'minute')
                                    .format('HH')
                            )}
                            maxMinute={Number(
                                moment(checkBreak ? breakStart : closeHours, 'HH:mm')
                                    .subtract('15'.valueOf(), 'minute')
                                    .format('mm')
                            )}
                            className={styles.UserInput}
                        />
                        <TimePickerComponent
                            value={closeHours || '17:30'}
                            setValue={setCloseHours}
                            label={t('to')}
                            minuteToDisable={15}
                            minHour={Number(
                                moment(checkBreak ? breakEnd : openHours, 'HH:mm')
                                    .add('15'.valueOf(), 'minute')
                                    .format('HH')
                            )}
                            minMinute={Number(
                                moment(checkBreak ? breakEnd : openHours, 'HH:mm')
                                    .add('15'.valueOf(), 'minute')
                                    .format('mm')
                            )}
                            className={styles.UserInput}
                        />
                    </div>
                    <Checkbox checked={checkBreak} onChange={setBreakCheck} text={t('break')} />
                    {checkBreak && (
                        <div className={styles.Input}>
                            <TimePickerComponent
                                value={breakStart || '12:00'}
                                setValue={setBreakStart}
                                label={t('from')}
                                minuteToDisable={15}
                                minHour={Number(moment(openHours, 'HH:mm').add('15'.valueOf(), 'minute').format('HH'))}
                                minMinute={Number(
                                    moment(openHours, 'HH:mm').add('15'.valueOf(), 'minute').format('mm')
                                )}
                                maxHour={Number(
                                    moment(breakEnd, 'HH:mm').subtract('15'.valueOf(), 'minute').format('HH')
                                )}
                                maxMinute={Number(
                                    moment(breakEnd, 'HH:mm').subtract('15'.valueOf(), 'minute').format('mm')
                                )}
                                className={styles.UserInput}
                            />
                            <TimePickerComponent
                                value={breakEnd || '13:00'}
                                setValue={setBreakEnd}
                                label={t('to')}
                                minuteToDisable={15}
                                minHour={Number(moment(breakStart, 'HH:mm').add('15'.valueOf(), 'minute').format('HH'))}
                                minMinute={Number(
                                    moment(breakStart, 'HH:mm').add('15'.valueOf(), 'minute').format('mm')
                                )}
                                maxHour={Number(
                                    moment(closeHours, 'HH:mm').subtract('15'.valueOf(), 'minute').format('HH')
                                )}
                                maxMinute={Number(
                                    moment(closeHours, 'HH:mm').subtract('15'.valueOf(), 'minute').format('mm')
                                )}
                                className={styles.UserInput}
                            />
                        </div>
                    )}
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'confirm'}
                        onClick={updateDaycare}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default NewOpeningHours
